import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import { draftPayrollModelLabel } from './draftPayrollModelLabel';
import AppCard from '@nxsys/components/AppCard';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { Dropdown, Typography } from 'antd';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import { DownOutlined } from '@ant-design/icons';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { GetDraftPayrollListParams } from '@nxsys/store/slices/report/types';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import {
  getDraftPayrollBureauColumns,
  getDraftPayrollCISColumns,
  getDraftPayrollPayeUmbrellaColumns,
} from './columns';
import { getTableScrollX } from './tableScrollX';
import FilterModal, { FilterValues } from '../../components/FilterModal';
import { debounce, sumBy } from 'lodash';
import {
  useConfirmMultipleDraftPayroll,
  useDeleteDraftPayrollById,
  useDownloadAllDraftPayroll,
  useGetDraftPayroll,
  useReversalMultipleDraftPayroll,
  useSendEmailDraftPayslipMultiple,
  useVerifyPayslip,
  useViewDraftPayslipById,
} from '@nxsys/hooks/api/payroll';
import { showMessage } from '@nxsys/utils/message';
import { trackPayslipZip } from '@nxsys/utils/payslip-zip-download';
import { formatMoney } from '@nxsys/utils/number-helper';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import { buildDraftPayrollExport } from './draftPayrollExport';
import DownloadAllModal, {
  DownloadAllValues,
} from '../components/DownloadAllModal';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import usePayrollSocket from '@nxsys/hooks/usePayrollSocket';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import { IRELAND } from '@nxsys/store/constants/keys';
import PayslipVerdictModal from './PayslipVerdictModal';
import { isEmailDraftAllowed } from './draftPayslipEmail';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type DraftPayrollReportProps = {
  paymentModels: PAYMENT_MODEL_ENUM[];
};

const DraftPayrollReport: React.FC<DraftPayrollReportProps> = ({
  paymentModels,
}) => {
  const { messages, formatMessage } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const isCisReportPage = paymentModels.includes(PAYMENT_MODEL_ENUM.CIS);
  const { isBureau, payrollCountry, user, isBureauEmployer } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : isBureau
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : isCisReportPage
        ? [PAYMENT_MODEL_ENUM.CIS, PAYMENT_MODEL_ENUM.NON_CIS]
        : [
            PAYMENT_MODEL_ENUM.UMBRELLA,
            PAYMENT_MODEL_ENUM.PAYEE,
            PAYMENT_MODEL_ENUM.HYBRID,
            PAYMENT_MODEL_ENUM.PAYE_HYBRID,
            PAYMENT_MODEL_ENUM.PAYE_FTH,
          ];

  const dispatch = useDispatch();
  const { draftPayrollList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } = draftPayrollList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<DraftPayroll[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);
  // NX-4971 — advisory payslip verifier (view verdict detail / re-run).
  const [verdictRecord, setVerdictRecord] = useState<DraftPayroll | null>(null);
  // Draft ids whose verification was just re-requested — show the spinner
  // immediately (optimistic), before the worker + completion socket round-trip.
  const [rerunningIds, setRerunningIds] = useState<string[]>([]);
  const { run: verifyPayslip, loading: isVerifyLoading } =
    useVerifyPayslip();
  // Rows with an optimistic RUNNING badge overlaid for just-re-run drafts.
  const displayList = useMemo(
    () =>
      (list ?? []).map((row: DraftPayroll) =>
        rerunningIds.includes(row.id)
          ? {
              ...row,
              payslipVerificationRef: {
                ...row.payslipVerificationRef,
                status: 'RUNNING' as const,
                verdict: null,
              },
            }
          : row,
      ),
    [list, rerunningIds],
  );
  const handleViewVerdict = useCallback(
    (record: DraftPayroll) => setVerdictRecord(record),
    [],
  );

  const { run: getDraftPayroll, loading: isGetDraftPayrollLoading } =
    useGetDraftPayroll();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={formatMessage(
            { id: 'common.draftPayrollWithModel' },
            {
              model: draftPayrollModelLabel({
                isCisReportPage,
                isBureau,
                isIreland,
              }),
            },
          )}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        {
          key: 'confirm',
          label: messages['common.confirm'],
          onClick: handleConfirmSelectedDraftPayroll,
        },
        {
          key: 'delete',
          label: messages['common.delete'],
          onClick: handleReversalSelectedDraftPayroll,
        },
        {
          key: 'sendEmailDraftPayslip',
          label: messages['common.sendEmailDraftPayslip'],
          // Needs at least one company in the list that allows it.
          disabled: !(list ?? []).some(isEmailDraftAllowed),
          onClick: handleSendDraftPayslipSelected,
        },
        // NX-4971 — advisory bulk verify; super-admin only, mirroring the Verifier column.
        ...(user?.isSuperAdmin
          ? [
              {
                key: 'verifyPayslips',
                label: messages['common.verifyPayslips'],
                onClick: handleVerifyPayslipSelected,
              },
            ]
          : []),
        {
          key: 'export',
          label: messages['common.export'],
          onClick: handleExportSelected,
        },
        {
          key: 'exportAll',
          label: messages['common.exportAll'],
          onClick: handleExportAll,
        },
        {
          key: 'downloadAll',
          label: messages['common.downloadAll'],
          onClick: () => setIsOpenDownloadAllModal(true),
        },
      ],
    };
    return (
      <Dropdown menu={menuProps}>
        <AppButton
          type="primary"
          name={messages['common.action']}
          rightIcon={<DownOutlined />}
        />
      </Dropdown>
    );
  };

  const handleFetchData = useCallback(
    (params: GetDraftPayrollListParams) => {
      const newParams = {
        ...params,
        paymentModels: validPaymentModels,
        payrollApproved: isBureauEmployer ? true : undefined,
      };

      newParams.companyRefs = resolveFilterCompanyIds(
        params.companyRefs,
        isBureauEmployer,
        user?.companyUserIds,
      );

      dispatch(reportActions.getDraftPayrollList(newParams));
    },
    [dispatch, isBureauEmployer, user?.companyUserIds, validPaymentModels],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: FilterValues) => {
    const params: GetDraftPayrollListParams = {
      ...queryParams,
      paymentStartDate: values.paymentDateFrom
        ? values.paymentDateFrom.format('MM/DD/YYYY')
        : undefined,
      paymentEndDate: values.paymentDateTo
        ? values.paymentDateTo.format('MM/DD/YYYY')
        : undefined,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
      batchFrom: values.batchFrom,
      batchTo: values.batchTo,
      companyRefs: values.companies?.map((i) => i.value),
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const totalTimesheetValuesSelected = selectedRows.length
    ? sumBy(selectedRows, (item: DraftPayroll) => Number(item.timesheetValue))
    : 0;

  const { run: viewDraftPayslipById, loading: isViewDraftPayslipByIdLoading } =
    useViewDraftPayslipById();

  const handleViewDraftPayslipById = (id: string) => {
    viewDraftPayslipById(id).then((res) => {
      if (res.success) {
        window.open(res.data, '_blank');
      } else {
        showMessage({
          type: 'error',
          content: messages['common.somethingWentWrong'],
        });
      }
    });
  };

  const handleExport = (data: DraftPayroll[]) => {
    const { headers, dataExport } = buildDraftPayrollExport({
      data,
      columns: tableColumns,
      payrollCountry,
      isCisReportPage,
    });

    const csvExporter = new ExportToCsv({
      filename: `Draft-payroll-export-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getDraftPayroll({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
      paymentModels: validPaymentModels,
    }).then((res) => {
      if (res.success) {
        handleExport(res.data);
      }
    });
  };

  const [isOpenDownloadAllModal, setIsOpenDownloadAllModal] = useState(false);
  const {
    run: downloadAllDraftPayroll,
    loading: isDownloadAllDraftPayrollLoading,
  } = useDownloadAllDraftPayroll();
  const handleDownloadAll = async (values: DownloadAllValues) => {
    const res = await downloadAllDraftPayroll({
      companyPaymentId: values.companyPayment?.value || '',
      agencyId: values.agency?.value || '',
      taxYear: values.taxYear,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
      paymentModels: validPaymentModels,
    });
    if (res.success) {
      trackPayslipZip(res.data.jobId, res.data.total);
    }
  };

  const {
    run: confirmMultipleDraftPayroll,
    loading: isConfirmMultipleDraftPayrollLoading,
  } = useConfirmMultipleDraftPayroll();
  const handleConfirmMultipleDraftPayroll = (ids: string[]) => {
    AppConfirmation.open({
      title: messages['common.confirmDraftPayroll'],
      paragraph: messages['common.areYouSureYouWantToConfirm'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.confirm'],
      onConfirm: () => {
        confirmMultipleDraftPayroll(ids).then((res) => {
          if (res.success) {
            showMessage({
              type: 'success',
              content: messages['common.draftPayrollConfirmed'],
            });
            refetchData();
          }
        });
      },
    });
  };
  const handleConfirmDraftPayroll = (id: string) => {
    handleConfirmMultipleDraftPayroll([id]);
  };
  const handleConfirmSelectedDraftPayroll = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleConfirmMultipleDraftPayroll(selectedRows.map((item) => item.id));
  };

  const {
    run: reversalMultipleDraftPayroll,
    loading: isReversalMultipleDraftPayrollLoading,
  } = useReversalMultipleDraftPayroll();
  const handleReversalMultipleDraftPayroll = (data: DraftPayroll[]) => {
    AppConfirmation.open({
      title: messages['common.deleteDraftPayroll'],
      paragraph: messages['common.areYouSureYouWantToDelete'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.delete'],
      onConfirm: () => {
        const ids = data.map((item) => item.id);
        reversalMultipleDraftPayroll({
          draftPayrollIds: ids,
          isDraft: true,
          payrollIds: [],
        }).then(() => {
          showMessage({
            type: 'info',
            content: messages['common.draftPayrollDeleteSuccessPending'],
          });
        });
      },
    });
  };
  const handleReversalSelectedDraftPayroll = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleReversalMultipleDraftPayroll(selectedRows);
  };
  usePayrollSocket({
    eventName: `generate_confirm-draft-payroll-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.draftPayrollConfirmed'],
      });
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.draftPayrollConfirmFailed'],
      });
    },
  });
  useNotificationSocket({
    eventName: `reversal-draft-payroll-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.draftPayrollReversalSuccess'],
      });
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.draftPayrollReversalFailed'],
      });
    },
  });
  // NX-4971 — advisory verifier verdicts arrive async on the HEAVY queue.
  // Refresh the list so the badge flips checking → verdict. Advisory, so silent.
  usePayrollSocket({
    eventName: `payslip-verify-${user?.id}`,
    onSuccess: () => {
      // Batch finished → drop the optimistic spinners and pull the real verdicts.
      setRerunningIds([]);
      refetchData();
    },
  });
  const handleRerunVerification = (record: DraftPayroll) => {
    setVerdictRecord(null); // close the verdict modal immediately on re-run
    // Optimistic: flip this row's badge to RUNNING immediately (no wait for the
    // worker); the completion socket clears it and refetches the real verdict.
    setRerunningIds((prev) =>
      prev.includes(record.id) ? prev : [...prev, record.id],
    );
    verifyPayslip({ draftPayrollIds: [record.id] })
      .then((res) => {
        if (res?.success) {
          showMessage({
            type: 'info',
            content:
              'Re-verification requested. The verdict will update shortly.',
          });
        } else {
          // Didn't queue — roll back the optimistic spinner.
          setRerunningIds((prev) => prev.filter((id) => id !== record.id));
        }
      })
      .catch(() => {
        setRerunningIds((prev) => prev.filter((id) => id !== record.id));
      });
  };

  // NX-4971 — bulk queue advisory verification for the selected drafts (same
  // endpoint as the per-row re-run, just an array of draft ids).
  const {
    run: verifyPayslipMultiple,
    loading: isVerifyPayslipMultipleLoading,
  } = useVerifyPayslip();
  const handleVerifyPayslipMultiple = (data: DraftPayroll[]) => {
    const ids = data.map((item) => item.id);
    // Optimistic: flip every selected row's badge to RUNNING immediately (no wait
    // for the worker); the completion socket clears them and refetches verdicts.
    setRerunningIds((prev) => Array.from(new Set([...prev, ...ids])));
    verifyPayslipMultiple({ draftPayrollIds: ids })
      .then((res) => {
        if (res?.success) {
          const cisSkipped = (res.skipped ?? []).filter(
            (s) => s.reason === 'not_paye_umbrella',
          ).length;
          const content = cisSkipped
            ? `${res.queued ?? 0} ${messages['common.verifyPayslipQueued']} · ${cisSkipped} ${messages['common.verifyPayslipSkippedCis']}`
            : `${res.queued ?? 0} ${messages['common.verifyPayslipQueued']}`;
          showMessage({
            type: 'success',
            content,
          });
        } else {
          // Didn't queue — roll back the optimistic spinners.
          setRerunningIds((prev) => prev.filter((id) => !ids.includes(id)));
        }
      })
      .catch(() => {
        setRerunningIds((prev) => prev.filter((id) => !ids.includes(id)));
      });
  };
  const handleVerifyPayslipSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleVerifyPayslipMultiple(selectedRows);
  };

  // NX-6289: email draft payslips before the bank file.
  const {
    run: sendEmailDraftPayslipMultiple,
    loading: isSendEmailDraftPayslipMultipleLoading,
  } = useSendEmailDraftPayslipMultiple();
  const handleSendDraftPayslipMultiple = (data: DraftPayroll[]) => {
    // Only the rows whose company allows emailing draft payslips are sent.
    const allowed = data.filter(isEmailDraftAllowed);
    if (!allowed.length) {
      showMessage({
        type: 'warning',
        content: messages['common.emailDraftPayslipDisabled'],
      });
      return;
    }
    sendEmailDraftPayslipMultiple(allowed.map((item) => item.id)).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.emailSentDraftPayslipPending'],
        });
      }
    });
  };
  const handleSendDraftPayslip = (record: DraftPayroll) => {
    handleSendDraftPayslipMultiple([record]);
  };
  const handleSendDraftPayslipSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleSendDraftPayslipMultiple(selectedRows);
  };
  useNotificationSocket({
    eventName: `send-mail-draft-self-bill-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.emailSentDraftPayslipSuccess'],
      });
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.emailSentDraftPayslipFailed'],
      });
    },
  });

  const {
    run: deleteDraftPayrollById,
    loading: isDeleteDraftPayrollByIdLoading,
  } = useDeleteDraftPayrollById();
  const handleDeleteDraftPayroll = (id: string) => {
    AppConfirmation.open({
      title: messages['common.deleteDraftPayroll'],
      paragraph: messages['common.areYouSureYouWantToDelete'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.delete'],
      onConfirm: () => {
        deleteDraftPayrollById(id).then((res) => {
          if (res.success) {
            showMessage({
              type: 'success',
              content: messages['common.draftPayrollDeleted'],
            });
            refetchData();
          }
        });
      },
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.paymentStartDate ||
    !!queryParams?.paymentEndDate ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.batchFrom ||
    !!queryParams?.batchTo;

  const tableColumns = useMemo(
    () =>
      isCisReportPage
        ? getDraftPayrollCISColumns({
            messages,
            payrollCountry,
            handleViewDraftPayslipById,
            handleConfirmDraftPayroll,
            handleDeleteDraftPayroll,
            handleSendDraftPayslip,
            isBureau,
          })
        : isBureau
          ? getDraftPayrollBureauColumns({
              messages,
              payrollCountry,
              handleViewDraftPayslipById,
              handleConfirmDraftPayroll,
              handleDeleteDraftPayroll,
              handleSendDraftPayslip,
              handleViewVerdict: user?.isSuperAdmin
                ? handleViewVerdict
                : undefined,
            })
          : getDraftPayrollPayeUmbrellaColumns({
              messages,
              payrollCountry,
              handleViewDraftPayslipById,
              handleConfirmDraftPayroll,
              handleDeleteDraftPayroll,
              handleSendDraftPayslip,
              handleViewVerdict: user?.isSuperAdmin
                ? handleViewVerdict
                : undefined,
              isBureau,
            }),
    [
      isCisReportPage,
      isBureau,
      messages,
      payrollCountry,
      handleViewDraftPayslipById,
      handleConfirmDraftPayroll,
      handleDeleteDraftPayroll,
      handleSendDraftPayslip,
      handleViewVerdict,
      user?.isSuperAdmin,
    ],
  );

  const tableScrollX = useMemo(
    () => getTableScrollX(tableColumns),
    [tableColumns],
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isViewDraftPayslipByIdLoading ||
        isGetDraftPayrollLoading ||
        isDownloadAllDraftPayrollLoading ||
        isConfirmMultipleDraftPayrollLoading ||
        isReversalMultipleDraftPayrollLoading ||
        isDeleteDraftPayrollByIdLoading ||
        isSendEmailDraftPayslipMultipleLoading ||
        isVerifyPayslipMultipleLoading
      }
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              {selectedRows.length > 0 && (
                <Typography.Text>
                  {messages['common.totalTimesheetValue']}:{' '}
                  {formatMoney(
                    totalTimesheetValuesSelected,
                    showCurrencyByCountry(payrollCountry),
                  )}
                </Typography.Text>
              )}
              <TaxYearSelect
                initValue={currentTaxYear}
                onFilter={(value) =>
                  handleFetchData({ ...queryParams, taxYear: value.taxYear })
                }
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={displayList}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: tableScrollX, y: 700 }}
            columns={tableColumns}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <FilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
        isBureau={isBureau}
      />
      <DownloadAllModal
        isOpen={isOpenDownloadAllModal}
        toggle={() => setIsOpenDownloadAllModal(!isOpenDownloadAllModal)}
        onDownload={handleDownloadAll}
        initialValues={{
          taxYear: queryParams?.taxYear ?? currentTaxYear,
        }}
      />
      <PayslipVerdictModal
        isOpen={!!verdictRecord}
        toggle={() => setVerdictRecord(null)}
        record={verdictRecord}
        onRerun={handleRerunVerification}
        rerunLoading={isVerifyLoading}
        onSaved={refetchData}
      />
    </AppsContainer>
  );
};

export default DraftPayrollReport;
